import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";

const API = "https://admin.shopaholics.ge/api";

/* ---------- Styled ---------- */
const Page = styled.div`
  max-width: 1080px;
  margin: auto 60px;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
  padding: 24px;
  border: 1px solid #f0f0f0;
`;

const TitleRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 18px;
  h2 { margin: 0; font-size: 20px; font-weight: 800; letter-spacing: 0.2px; }
  small { color: #888; }
`;

const FormGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 18px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  label { font-size: 13px; color: #333; font-weight: 700; }
  input[type="text"], input[type="number"], textarea {
    border: 1px solid #e6e6e6; background: #fafafa; border-radius: 12px;
    padding: 12px 14px; outline: none; font-size: 14px; transition: 0.15s ease;
  }
  textarea { min-height: 110px; resize: vertical; }
  input:focus, textarea:focus { border-color: #bdbdbd; background: #fff; box-shadow: 0 6px 16px rgba(0,0,0,0.04); }
`;

const SwitchRow = styled.div`
  display: flex; gap: 16px; align-items: center; padding-top: 6px;
  .switch { display: inline-flex; align-items: center; gap: 8px; user-select: none; }
  .switch input { transform: scale(1.15); }
  .switch span { font-size: 14px; color: #333; }
`;

const Preview = styled.div`
  display: flex; gap: 16px; align-items: center;
  .img { width: 120px; height: 78px; background: #f5f5f7; border: 1px dashed #e6e6e6; border-radius: 12px; overflow: hidden; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Actions = styled.div`
  display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px;
  button { border: 0; border-radius: 12px; padding: 12px 16px; font-weight: 700; cursor: pointer; transition: 0.15s ease; }
  .save { background: #111; color: #fff; }
  .save:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,0.13); }
  .ghost { background: #f4f4f4; color: #111; }
  .ghost:hover { background: #eaeaea; }
`;

const UploadRow = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center;
  .hint { font-size: 12px; color: #666; }
`;

const Progress = styled.div`
  height: 6px; background: #f2f2f2; border-radius: 999px; overflow: hidden;
  > span { display: block; height: 100%; background: #111; width: ${p=>p.value||0}%; transition: width .2s; }
  margin-top: 6px;
`;

const ToastWrap = styled.div`
  position: fixed; inset: 0; display: flex; align-items: flex-start; justify-content: center; pointer-events: none;
  padding-top: 20px;
`;
const Toast = styled.div`
  background: #111; color: #fff; padding: 12px 16px; border-radius: 12px; box-shadow: 0 12px 30px rgba(0,0,0,0.25);
  font-weight: 700; pointer-events: auto;
`;

/* ---------- Helpers ---------- */
const slugify = (v) =>
  v.toString().trim().toLowerCase()
    .replace(/[\s\._]+/g, "-")
    .replace(/[^a-z0-9\-]/g, "")
    .replace(/\-+/g, "-");

const initialForm = {
  slug: "",
  name_ka: "",
  name_en: "",
  description_ka: "",
  description_en: "",
  cover_image: "",
  is_active: 1,
  sort_order: 0,
};

export default function CollectionsForm({ selectedId, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(""); // success message
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);

  useEffect(() => {
    if (selectedId) {
      axios.get(`${API}/collections/list.php`).then(({ data }) => {
        // თუ endpoint აბრუნებს {success, data:[...]} — მოვარგოთ
        const arr = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        const item = arr.find((x) => String(x.id) === String(selectedId));
        if (item) setForm({ ...initialForm, ...item });
      });
    } else {
      setForm(initialForm);
    }
  }, [selectedId]);

  const autoSlug = () => {
    if (!form.slug && form.name_en) {
      setForm((f) => ({ ...f, slug: slugify(f.name_en) }));
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const save = async () => {
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (selectedId) fd.append("id", selectedId);
      const url = selectedId ? `${API}/collections/update.php` : `${API}/collections/create.php`;
      const res = await axios.post(url, fd);

      // წარმატება
      showToast(selectedId ? "ცვლილებები შენახულია" : "კოლექცია დამატებულია");
      // გასუფთავება: მოთხოვნილია რომ წარმატების შემდეგ ველები გაიწმინდოს
      setForm(initialForm);
      // გარეთაც აცნობე, თუ სჭირდება განახლება
      onSaved?.(res?.data);
    } catch (e) {
      showToast("შეცდომა შენახვისას");
    } finally {
      setSaving(false);
    }
  };

  // ქოვერის ატვირთვა: api/uploads/collections-covers/upload.php -> {success:true, url:"..."}
  const onCoverFile = async (file) => {
    if (!file) return;
    try {
      setCoverUploading(true);
      setCoverProgress(0);
      const fd = new FormData();
      fd.append("file", file);
      // სურვილისამებრ: სლაგი დასახელებად
      if (form.slug) fd.append("slug", form.slug);

      const { data } = await axios.post(
        `${API}/uploads/collections-covers/upload.php`,
        fd,
        {
          onUploadProgress: (e) => {
            if (!e.total) return;
            const val = Math.round((e.loaded * 100) / e.total);
            setCoverProgress(val);
          },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const url = data?.url || data?.data?.url || "";
      if (url) {
        setForm((f) => ({ ...f, cover_image: url }));
        showToast("ქოვერი ატვირთულია");
      } else {
        showToast("ატვირთვა ვერ დასრულდა");
      }
    } catch (e) {
      showToast("ატვირთვის შეცდომა");
    } finally {
      setCoverUploading(false);
      setCoverProgress(0);
    }
  };

  return (
    <Page>
      {toast && (
        <ToastWrap>
          <Toast>{toast}</Toast>
        </ToastWrap>
      )}

      <Card>
        <TitleRow>
          <h2>კოლექცია</h2>
          <small>{selectedId ? "რედაქტირება" : "ახალი კოლექცია"}</small>
        </TitleRow>

        <FormGrid>
          <Field>
            <label>Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              onBlur={autoSlug}
              placeholder="classic-collection"
            />
          </Field>

          <Field>
            <label>სახელი (KA)</label>
            <input
              type="text"
              value={form.name_ka}
              onChange={(e) => setForm({ ...form, name_ka: e.target.value })}
              placeholder="კლასიკური კოლექცია"
            />
          </Field>

          <Field>
            <label>Name (EN)</label>
            <input
              type="text"
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
              onBlur={autoSlug}
              placeholder="Classic Collection"
            />
          </Field>

          <Field>
            <label>Sort order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: +e.target.value })}
              placeholder="0"
            />
          </Field>

          <Field style={{ gridColumn: "1 / -1" }}>
            <label>აღწერა (KA)</label>
            <textarea
              value={form.description_ka}
              onChange={(e) => setForm({ ...form, description_ka: e.target.value })}
              placeholder="კოლექციის ქართული აღწერა…"
            />
          </Field>

          <Field style={{ gridColumn: "1 / -1" }}>
            <label>Description (EN)</label>
            <textarea
              value={form.description_en}
              onChange={(e) => setForm({ ...form, description_en: e.target.value })}
              placeholder="English description…"
            />
          </Field>

          {/* Cover upload + manual url fallback */}
          <Field>
            <label>Cover image</label>
            <UploadRow>
              <input
                type="text"
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                placeholder="https://…/cover.jpg (ატვირთვის შემთხვევაში ავტომატურად შეივსება)"
              />
              <label className="ghost" style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid #eee", background: "#f9f9f9", cursor: "pointer" }}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => onCoverFile(e.target.files?.[0])}
                />
                ატვირთე
              </label>
            </UploadRow>
            {coverUploading && (
              <Progress value={coverProgress}>
                <span />
              </Progress>
            )}
            <div className="hint">ფაილი აიტვირთება: <b>api/uploads/collections-covers/</b> და მიღებული URL ავტომატურად ჩაიწერება.</div>
          </Field>

          <Field>
            <label>Preview</label>
            <Preview>
              <div className="img">
                {form.cover_image ? <img src={form.cover_image} alt="cover" /> : null}
              </div>
            </Preview>
          </Field>
        </FormGrid>

        <SwitchRow>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
            />
            <span>Active</span>
          </label>
        </SwitchRow>

        <Actions>
          <button className="ghost" type="button" onClick={() => onSaved?.()}>
            Cancel
          </button>
          <button className="save" type="button" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </Actions>
      </Card>
    </Page>
  );
}
